import { DateTime } from 'luxon'

import {
  MOCK_BESS_RESOURCES,
  MOCK_LOCAL_PQ_MARKETS,
  MOCK_LOCAL_PQ_RESOURCE_SET,
  MOCK_LOCAL_PQ_RUNS,
  MOCK_RESERVE_RESOURCES,
  MOCK_SEARCHABLE_RESOURCES,
} from '@/features/prequalification/localControl/mocks/localPq'
import type {
  AddResourceSetResourcesRequest,
  ApiClearQueueResponse,
  ApiCreateLocalPqResourceSetResponse,
  ApiRunResourceSetResponse,
  ApiStartLocalPqRunResponse,
  CreateLocalPqResourceSetRequest,
  LocalPqResourceSet,
  LocalPqRun,
  PqCsvDownload,
  PqMarketCatalogueEntry,
  PqTestName,
  ResourceSetMember,
  RunResourceSetMarketRequest,
  RunResourceSetTestRequest,
  StartLocalPqRunRequest,
  UpdateLocalPqResourceSetRequest,
} from '@/features/prequalification/localControl/types'
import { marketStatuses, resourceSetPhase } from '@/features/prequalification/localControl/utils/aggregation'
import { testsOfMarket } from '@/features/prequalification/localControl/utils/marketCatalogue'
import type { PqPhase, PrequalificationResourceSet } from '@/features/prequalification/types'
import { isTerminalPhase } from '@/features/prequalification/types'

/**
 * Mock implementations of the local-PQ endpoints, backed by a small in-memory
 * store keyed by `resourceSetId` so the redesigned flow is coherent end to end: create
 * a resource set → its detail page shows the resources with no runs → run a test or a
 * whole market → runs appear and *settle* toward terminal phases on each refetch
 * (no timers; the runs query polls while anything is active). The store resets
 * on a full page reload (and via `__resetPqMockStore` in tests).
 *
 * Permanent mock (no backend exists yet): `downloadResourceSetCsv` — the combined
 * resource-set CSV is deferred (LC-196) and the UI has no trigger for it. Every
 * other endpoint here mirrors the real `localPq` contract (so the whole flow stays
 * demoable while `USE_LOCAL_PQ_MOCKS` is on) and is swappable to its real
 * counterpart from `pqClient`, the single swap point. Resource search is *not*
 * mocked here — it runs live against the Resources Manager API (see `resourceSearch`).
 */

type StoreEntry = {
  resourceSet: LocalPqResourceSet
  runs: LocalPqRun[]
}

const store = new Map<string, StoreEntry>()
/**
 * Names of resources the live search has surfaced this session, keyed by id. The
 * real backend resolves a resource set's resource ids → names server-side; with no such
 * backend yet, `createResourceSet`/`addResourceSetResources` only receive ids, so we lean on
 * the search (the one live call) to teach us the names. Without this, a resource set
 * built from live-searched resources would fall back to showing bare ids.
 */
const knownResources = new Map<string, ResourceSetMember>()
let runSeq = 0
let resourceSetSeq = 0

function nextRunId(): string {
  runSeq += 1
  return `mock-run-${runSeq}`
}

function nextResourceSetId(): string {
  resourceSetSeq += 1
  return `mock-resource-set-${String(resourceSetSeq).padStart(4, '0')}-4a5b-8c6d-1e2f3a4b5c6d`
}

type RunSpec = { testName: PqTestName; phase: PqPhase }

type ResourceSetSeed = {
  resourceSetId: string
  name: string
  resources: ResourceSetMember[]
  startOffsetDays: number
  runs: RunSpec[]
}

const SEEDS: ResourceSetSeed[] = [
  {
    resourceSetId: 'b1a2c3d4-0002-4a5b-8c6d-1e2f3a4b5c6d',
    name: 'Fortum SE4 — BESS qualification',
    resources: MOCK_BESS_RESOURCES,
    startOffsetDays: 3,
    runs: [{ testName: 'SINE_25_N', phase: 'SCHEDULED' }],
  },
  {
    resourceSetId: 'b1a2c3d4-0003-4a5b-8c6d-1e2f3a4b5c6d',
    name: 'Helen FI — reserve units',
    resources: MOCK_RESERVE_RESOURCES,
    startOffsetDays: -2,
    runs: [{ testName: 'LER_D_UP', phase: 'PASSED' }],
  },
  {
    resourceSetId: 'b1a2c3d4-0004-4a5b-8c6d-1e2f3a4b5c6d',
    name: 'Ad-hoc unit — 0.5 MW trial',
    resources: [{ resourceId: 'd4e6f8b0-5c55-4cae-bf5e-6cb091de5001', name: 'Ad-hoc unit — 0.5 MW' }],
    startOffsetDays: -4,
    runs: [{ testName: 'RAMP_DOWN_AND_N', phase: 'FAILED' }],
  },
]

function buildSeedEntry(seed: ResourceSetSeed, now: DateTime): StoreEntry {
  const startAt = now.plus({ days: seed.startOffsetDays })
  const requestedAt = startAt.minus({ hours: 2 })

  const runs = seed.runs.flatMap((spec): LocalPqRun[] =>
    seed.resources.map((resource) => {
      const scheduled = spec.phase === 'SCHEDULED'
      const settledAt = startAt.plus({ minutes: 15 })
      return {
        id: nextRunId(),
        resourceSetId: seed.resourceSetId,
        resourceId: resource.resourceId,
        resourceName: resource.name,
        testName: spec.testName,
        requestedAt,
        startAt,
        phase: spec.phase,
        lastStatusAt: scheduled ? null : settledAt,
        updatedAt: scheduled ? requestedAt : settledAt,
      }
    }),
  )

  const updatedAt = runs.reduce<DateTime>((latest, run) => DateTime.max(latest, run.updatedAt), requestedAt)

  return {
    resourceSet: {
      id: seed.resourceSetId,
      scope: 'local-control',
      name: seed.name,
      resources: seed.resources.map((resource) => ({ resourceId: resource.resourceId, name: resource.name })),
      requestedAt,
      startAt: null,
      updatedAt,
    },
    runs,
  }
}

function seedStore(): void {
  store.clear()
  knownResources.clear()
  runSeq = 0
  resourceSetSeq = 0

  store.set(MOCK_LOCAL_PQ_RESOURCE_SET.id, {
    resourceSet: {
      ...MOCK_LOCAL_PQ_RESOURCE_SET,
      resources: MOCK_LOCAL_PQ_RESOURCE_SET.resources.map((resource) => ({ ...resource })),
    },
    runs: MOCK_LOCAL_PQ_RUNS.map((run) => ({ ...run })),
  })

  const now = DateTime.now()
  for (const seed of SEEDS) {
    store.set(seed.resourceSetId, buildSeedEntry(seed, now))
  }
}

seedStore()

/** Test seam: re-seed the in-memory store between tests. */
export function __resetPqMockStore(): void {
  seedStore()
}

function ensureResourceSet(resourceSetId: string): StoreEntry {
  const existing = store.get(resourceSetId)
  if (existing) return existing

  const now = DateTime.now()
  const entry: StoreEntry = {
    resourceSet: {
      id: resourceSetId,
      scope: 'local-control',
      name: `Resource set ${resourceSetId.slice(0, 8)}`,
      resources: [],
      requestedAt: now,
      startAt: null,
      updatedAt: now,
    },
    runs: [],
  }
  store.set(resourceSetId, entry)
  return entry
}

/** Advance one non-terminal run a single phase along ENQUEUED → STARTED → PASSED. */
function settleRun(run: LocalPqRun, now: DateTime): LocalPqRun {
  if (isTerminalPhase(run.phase)) return run

  const next = ((): PqPhase => {
    switch (run.phase) {
      case 'SCHEDULED':
        return run.startAt !== null && run.startAt > now ? 'SCHEDULED' : 'ENQUEUED'
      case 'ENQUEUED':
        return 'STARTED'
      case 'STARTED':
        return 'PASSED'
      default:
        return run.phase
    }
  })()

  if (next === run.phase) return run
  return { ...run, phase: next, lastStatusAt: now, updatedAt: now }
}

function upsertRun(entry: StoreEntry, run: LocalPqRun): void {
  const index = entry.runs.findIndex(
    (existing) => existing.resourceId === run.resourceId && existing.testName === run.testName,
  )
  if (index >= 0) {
    entry.runs[index] = run
  } else {
    entry.runs.push(run)
  }
}

function createRun(
  resourceSetId: string,
  resource: ResourceSetMember,
  testName: PqTestName,
  startAtIso?: string | null,
): LocalPqRun {
  const now = DateTime.now()
  const startAt = startAtIso ? DateTime.fromISO(startAtIso) : now
  const scheduled = startAt > now

  return {
    id: nextRunId(),
    resourceSetId,
    resourceId: resource.resourceId,
    resourceName: resource.name,
    testName,
    requestedAt: now,
    startAt,
    phase: scheduled ? 'SCHEDULED' : 'ENQUEUED',
    lastStatusAt: scheduled ? null : now,
    updatedAt: now,
  }
}

/**
 * Record the resources a live search returned so later id-only calls (create /
 * add) can recover their names. Mirrors what the real backend resolves on its
 * own; a no-op shape against the real client. Ids whose name is just the id
 * (the search's own fallback for a nameless resource) are skipped — there's
 * nothing to recover.
 */
export function rememberResources(resources: ResourceSetMember[]): void {
  for (const resource of resources) {
    if (resource.name !== resource.resourceId) knownResources.set(resource.resourceId, resource)
  }
}

function resolveResource(resourceId: string): ResourceSetMember {
  const remembered = knownResources.get(resourceId)
  if (remembered) return remembered

  const known = MOCK_SEARCHABLE_RESOURCES.find((resource) => resource.resourceId === resourceId)
  return known ?? { resourceId, name: resourceId }
}

function toResourceSet(entry: StoreEntry): PrequalificationResourceSet {
  const { resourceSet, runs } = entry
  const lastStatusAt = runs.reduce<DateTime | null>(
    (latest, run) => (run.lastStatusAt && (!latest || run.lastStatusAt > latest) ? run.lastStatusAt : latest),
    null,
  )

  const markets = marketStatuses(runs, MOCK_LOCAL_PQ_MARKETS)
  return {
    id: resourceSet.id,
    scope: resourceSet.scope,
    name: resourceSet.name,
    resourceCount: resourceSet.resources.length,
    markets,
    phase: resourceSetPhase(markets),
    requestedAt: resourceSet.requestedAt,
    startAt: resourceSet.startAt,
    lastStatusAt: lastStatusAt ?? resourceSet.requestedAt,
  }
}

/**
 * Stand-in for the backend's default-name rule when the caller leaves `name`
 * blank: the count of resources. The real service should mirror this
 * (documented in the design doc).
 */
function defaultResourceSetName(count: number): string {
  return `${count} ${count === 1 ? 'resource' : 'resources'}`
}

// ── Endpoints ────────────────────────────────────────────────────────────────

export function listMarkets(): Promise<PqMarketCatalogueEntry[]> {
  return Promise.resolve(MOCK_LOCAL_PQ_MARKETS)
}

/** Lists every resource set in the store as a resource set summary row. */
export function listResourceSets(): Promise<PrequalificationResourceSet[]> {
  return Promise.resolve(Array.from(store.values(), toResourceSet))
}

export function createResourceSet(
  request: CreateLocalPqResourceSetRequest,
): Promise<ApiCreateLocalPqResourceSetResponse> {
  const resourceSetId = nextResourceSetId()
  const now = DateTime.now()

  const resources: ResourceSetMember[] = request.resourceIds.map(resolveResource)
  const name = request.name?.trim() || defaultResourceSetName(resources.length)

  store.set(resourceSetId, {
    resourceSet: {
      id: resourceSetId,
      scope: 'local-control',
      name,
      resources,
      requestedAt: now,
      startAt: null,
      updatedAt: now,
    },
    runs: [],
  })

  return Promise.resolve({
    id: resourceSetId,
    message: `Mock resource set "${name}" created with ${resources.length} resources.`,
  })
}

export function getResourceSet(resourceSetId: string): Promise<LocalPqResourceSet> {
  return Promise.resolve(ensureResourceSet(resourceSetId).resourceSet)
}

export function updateResourceSet(resourceSetId: string, request: UpdateLocalPqResourceSetRequest): Promise<void> {
  const entry = ensureResourceSet(resourceSetId)
  entry.resourceSet = { ...entry.resourceSet, name: request.name, updatedAt: DateTime.now() }
  return Promise.resolve()
}

export function addResourceSetResources(resourceSetId: string, request: AddResourceSetResourcesRequest): Promise<void> {
  const entry = ensureResourceSet(resourceSetId)
  const existing = new Set(entry.resourceSet.resources.map((resource) => resource.resourceId))
  const additions = request.resourceIds.filter((id) => !existing.has(id)).map(resolveResource)
  entry.resourceSet = {
    ...entry.resourceSet,
    resources: [...entry.resourceSet.resources, ...additions],
    updatedAt: DateTime.now(),
  }
  return Promise.resolve()
}

export function removeResourceSetResource(resourceSetId: string, resourceId: string): Promise<void> {
  const entry = ensureResourceSet(resourceSetId)
  entry.resourceSet = {
    ...entry.resourceSet,
    resources: entry.resourceSet.resources.filter((resource) => resource.resourceId !== resourceId),
    updatedAt: DateTime.now(),
  }
  return Promise.resolve()
}

export function listResourceSetRuns(resourceSetId: string): Promise<LocalPqRun[]> {
  const entry = ensureResourceSet(resourceSetId)
  const now = DateTime.now()
  entry.runs = entry.runs.map((run) => settleRun(run, now))
  return Promise.resolve(entry.runs)
}

export function runResourceSetTest(
  resourceSetId: string,
  request: RunResourceSetTestRequest,
): Promise<ApiRunResourceSetResponse> {
  const entry = ensureResourceSet(resourceSetId)
  const runs = entry.resourceSet.resources.map((resource) =>
    createRun(resourceSetId, resource, request.testName, request.startAt),
  )
  for (const run of runs) upsertRun(entry, run)

  return Promise.resolve({
    resourceSetId,
    runIds: runs.map((run) => run.id),
    message: `Mock test ${request.testName} fanned out to ${runs.length} resources.`,
  })
}

export function runResourceSetMarket(
  resourceSetId: string,
  request: RunResourceSetMarketRequest,
): Promise<ApiRunResourceSetResponse> {
  const entry = ensureResourceSet(resourceSetId)
  const market = MOCK_LOCAL_PQ_MARKETS.find((candidate) => candidate.market === request.market)
  if (!market) {
    return Promise.reject(new Error(`Mock: unknown market ${request.market}`))
  }
  const runIds: string[] = []

  for (const testName of testsOfMarket(market)) {
    for (const resource of entry.resourceSet.resources) {
      const run = createRun(resourceSetId, resource, testName, request.startAt)
      upsertRun(entry, run)
      runIds.push(run.id)
    }
  }

  return Promise.resolve({
    resourceSetId,
    runIds,
    message: `Mock market ${request.market} fanned out to ${runIds.length} runs.`,
  })
}

export function startRun(request: StartLocalPqRunRequest): Promise<ApiStartLocalPqRunResponse> {
  const entry = ensureResourceSet(request.resourceSetId)
  const resource = entry.resourceSet.resources.find((candidate) => candidate.resourceId === request.resourceId) ?? {
    resourceId: request.resourceId,
    name: request.resourceId,
  }
  const run = createRun(request.resourceSetId, resource, request.testName, request.startAt)
  upsertRun(entry, run)

  return Promise.resolve({
    id: run.id,
    workflowId: `pq-${run.id}`,
    message: `Mock run accepted for resource ${request.resourceId}.`,
  })
}

export function cancelRun(runId: string): Promise<void> {
  const now = DateTime.now()
  for (const entry of store.values()) {
    const index = entry.runs.findIndex((run) => run.id === runId)
    if (index >= 0 && !isTerminalPhase(entry.runs[index].phase)) {
      entry.runs[index] = { ...entry.runs[index], phase: 'CANCELLED', lastStatusAt: now, updatedAt: now }
    }
  }
  return Promise.resolve()
}

export function cancelResourceSet(resourceSetId: string): Promise<void> {
  const entry = store.get(resourceSetId)
  if (entry) {
    const now = DateTime.now()
    entry.runs = entry.runs.map((run) =>
      isTerminalPhase(run.phase) ? run : { ...run, phase: 'CANCELLED', lastStatusAt: now, updatedAt: now },
    )
  }
  return Promise.resolve()
}

export function deleteResourceSet(resourceSetId: string): Promise<void> {
  store.delete(resourceSetId)
  return Promise.resolve()
}

export function clearQueue(resourceId: string): Promise<ApiClearQueueResponse> {
  return Promise.resolve({
    resourceId,
    workflowId: `clear-${resourceId}`,
    acked: true,
    message: 'Mock clear-queue acked.',
  })
}

function buildCsv(runs: LocalPqRun[]): string {
  const header = 'run_id;resource_id;resource_name;test_name;phase;last_status_at'
  const rows = runs.map((run) =>
    [run.id, run.resourceId, run.resourceName ?? '', run.testName, run.phase, run.lastStatusAt?.toISO() ?? ''].join(
      ';',
    ),
  )

  return [header, ...rows].join('\n')
}

export function downloadRunCsv(runId: string): Promise<PqCsvDownload> {
  for (const entry of store.values()) {
    const run = entry.runs.find((candidate) => candidate.id === runId)
    if (run) {
      return Promise.resolve({
        blob: new Blob([buildCsv([run])], { type: 'text/plain' }),
        filename: `local-pq-run-${runId}.csv`,
      })
    }
  }

  return Promise.resolve({
    blob: new Blob([buildCsv([])], { type: 'text/plain' }),
    filename: `local-pq-run-${runId}.csv`,
  })
}

/** Combined per-resource set CSV. Backend endpoint is missing; this is the mock. */
export function downloadResourceSetCsv(resourceSetId: string): Promise<PqCsvDownload> {
  const entry = store.get(resourceSetId)
  return Promise.resolve({
    blob: new Blob([buildCsv(entry?.runs ?? [])], { type: 'text/plain' }),
    filename: `local-pq-resource-set-${resourceSetId}.csv`,
  })
}

/** Combined per-test CSV across the resource set's resources. Mirrors the real (new) test-CSV endpoint. */
export function downloadTestCsv(resourceSetId: string, testName: PqTestName): Promise<PqCsvDownload> {
  const entry = store.get(resourceSetId)
  const testRuns = (entry?.runs ?? []).filter((run) => run.testName === testName)
  return Promise.resolve({
    blob: new Blob([buildCsv(testRuns)], { type: 'text/plain' }),
    filename: `local-pq-test-${testName}-${resourceSetId}.csv`,
  })
}
